import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 bg-white border-b-2 border-ink" data-testid="navbar">
            {/* Masthead */}
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <button
                        className="lg:hidden p-2 -ml-2"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                    </button>

                    <Link to="/" className="flex items-baseline gap-1 mx-auto lg:mx-0" data-testid="navbar-logo">
                        <span className="font-serif text-2xl sm:text-3xl font-black tracking-tight">{brand.site_name}</span>
                        <span className="w-2 h-2 bg-accent mb-1" />
                    </Link>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className="p-2 hover:text-accent transition-colors duration-150"
                            aria-label="Search"
                            data-testid="navbar-search-toggle"
                        >
                            <Search size={20} />
                        </button>
                        {user ? (
                            <div className="hidden sm:flex items-center gap-3">
                                <Link to="/account" data-testid="navbar-account" className="font-mono text-xs text-muted-foreground flex items-center gap-1 hover:text-accent transition-colors duration-150">
                                    <User size={14} /> {user.name?.split(" ")[0]}
                                </Link>
                                <button
                                    onClick={() => { logout(); navigate("/"); }}
                                    data-testid="navbar-logout"
                                    className="font-mono text-xs uppercase tracking-wider hover:text-accent transition-colors duration-150"
                                >
                                    Log out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link to="/login" data-testid="navbar-login" className="font-mono text-xs uppercase tracking-wider px-3 py-2 hover:text-accent transition-colors duration-150">
                                    Log in
                                </Link>
                                <Link to="/register" data-testid="navbar-register" className="font-mono text-xs uppercase tracking-wider px-4 py-2 bg-ink text-white hover:bg-accent transition-colors duration-150">
                                    Subscribe
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Category bar (desktop) */}
            <nav className="hidden lg:block border-t border-hair bg-white">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    <ul className="flex items-center gap-0">
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="relative group">
                                <Link
                                    to={`/category/${cat}`}
                                    data-testid={`nav-cat-${cat}`}
                                    className="flex items-center gap-1 px-4 py-3 font-mono text-xs font-semibold uppercase tracking-[0.08em] hover:text-accent transition-colors duration-150 border-r border-hair"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && <ChevronDown size={12} />}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-0 top-full w-56 bg-white border-2 border-ink invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 z-50">
                                        {SUBCATEGORIES.map((sub) => (
                                            <Link
                                                key={sub}
                                                to={`/category/personal-finance/${sub}`}
                                                data-testid={`nav-sub-${sub}`}
                                                className="block px-4 py-2.5 font-mono text-xs uppercase tracking-wider hover:bg-ink hover:text-white transition-colors duration-150 border-b border-hair last:border-0"
                                            >
                                                {SUBCATEGORY_LABELS[sub]}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </li>
                        ))}
                        <li className="ml-auto">
                            <Link to="/archive" data-testid="nav-archive" className="block px-4 py-3 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground hover:text-accent transition-colors duration-150">
                                Archive
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>

            {/* Search dropdown */}
            {searchOpen && (
                <div className="border-t border-hair bg-muted" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-2">
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search articles, markets, authors..."
                            data-testid="navbar-search-input"
                            className="flex-1 bg-white border border-ink px-4 py-3 font-sans text-sm focus:outline-none focus:ring-1 focus:ring-accent"
                        />
                        <button type="submit" data-testid="navbar-search-submit" className="bg-ink text-white px-6 font-mono text-xs uppercase tracking-wider hover:bg-accent transition-colors duration-150">
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="lg:hidden border-t border-hair bg-white" data-testid="mobile-menu">
                    <ul>
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="border-b border-hair">
                                <Link to={`/category/${cat}`} onClick={() => setMobileOpen(false)} className="block px-6 py-3 font-mono text-xs font-semibold uppercase tracking-wider">
                                    {CATEGORY_LABELS[cat]}
                                </Link>
                            </li>
                        ))}
                        <li className="border-b border-hair">
                            <Link to="/archive" onClick={() => setMobileOpen(false)} className="block px-6 py-3 font-mono text-xs uppercase tracking-wider text-muted-foreground">Archive</Link>
                        </li>
                        {!user && (
                            <li className="flex gap-2 p-4">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center border border-ink py-2 font-mono text-xs uppercase">Log in</Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center bg-ink text-white py-2 font-mono text-xs uppercase">Subscribe</Link>
                            </li>
                        )}
                        {user && (
                            <li className="p-4 space-y-2">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block w-full text-center border border-ink py-2 font-mono text-xs uppercase">My account</Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full border border-ink py-2 font-mono text-xs uppercase">Log out</button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
